import { type Message } from "@ai-sdk/react";
import { useSetToast } from "@app/components/Toast";
import { useS3FilesManagement } from "@app/hooks/createFileHook";
import { Chat, TitleChip } from "@app/shared/components/ai/Chat";
import { type NewS3File } from "@app/usecases/FileUseCases";
import { type SubmitParamEvent } from "@app/utils/ai/chat";
import { FlexCol } from "@design-system/Layout/Flex";
import { ResponseStatus, type ScoreValue } from "@generated/client/graphql";
import { type FC, Fragment, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
    useDocumentReviewerChat,
    useScoreResponse,
    useTitleGenerator,
} from "../../data";
import { useAgentBreadcrumb } from "../../useAgentBreadcrumb";
import { useDocumentReviewerAgentTranslations } from "./i18n";

export const DocumentReviewerAgent: FC = () => {
    const { agent_id } = useParams();
    const { t, keys } = useDocumentReviewerAgentTranslations();

    const {
        messages,
        input,
        handleInputChange,
        handleSubmit: _handleSubmit,
        status,
        reload,
        error,
        stop,
        append,
    } = useDocumentReviewerChat();

    const { scoreResponse } = useScoreResponse();

    const { setToastSuccess, setToastError } = useSetToast();

    const title = useTitleGenerator(messages, status, 4);
    useAgentBreadcrumb(agent_id ?? "", title);

    const { uploadFiletoS3, isLoading: isUploadLoading } =
        useS3FilesManagement();

    const [file, setFile] = useState<File | undefined>(undefined);

    const inputRef = useRef<HTMLInputElement>(null);

    const date = messages?.[0]?.createdAt || new Date();

    const exampleMessages = [
        t(keys.example_message_chip_policy_missing),
        t(keys.example_message_chip_policy_strengthen),
        t(keys.example_message_chip_policy_dei),
        t(keys.example_message_chip_policy_anti_corruption),
    ];

    const isLoading = status === "submitted";

    const isDisabled =
        status !== "ready" || (!input.length && !file) || !!error;

    const showStopButton = status === "streaming" || status === "submitted";

    const handleSubmit = async (e: SubmitParamEvent) => {
        let s3File: NewS3File | undefined;
        if (file) {
            s3File = await uploadFiletoS3(file);
            setFile(undefined);
        }
        if (inputRef.current) {
            inputRef.current.value = "";
        }
        _handleSubmit(e, {
            allowEmptySubmit: !!file,
            experimental_attachments: s3File
                ? [
                      {
                          name: s3File.name,
                          url: s3File.signedUrl,
                          contentType: s3File.mimeType,
                      },
                  ]
                : undefined,
        });
    };

    const handleScoreResponse = (messageId: string) => {
        return (scoreValue: ScoreValue) => {
            scoreResponse(
                { traceId: messageId, scoreValue },
                {
                    onSettled(data) {
                        if (
                            data?.scoreResponse.status ===
                            ResponseStatus.Success
                        ) {
                            setToastSuccess(t(keys.score_response_success));
                            return;
                        }
                        setToastError(t(keys.score_response_error));
                    },
                },
            );
        };
    };

    const handleExampleMessageChipClick = (message: string) => {
        return () => {
            append({
                id: crypto.randomUUID(),
                role: "user",
                content: message,
            });
        };
    };

    return (
        <FlexCol
            w="full"
            alignItems="center"
            className="h-[calc(100%-8px)] bg-white relative"
        >
            <TitleChip
                title={t(keys.title_chip_title)}
                className="absolute top-0 left-0"
            />
            <Chat>
                {!messages.length ? (
                    <Chat.EmptyState title={t(keys.empty_state_title)} />
                ) : (
                    <Chat.Content>
                        <Chat.Date date={date} />

                        {messages.map((message, idx) =>
                            message.role === "assistant" ? (
                                <Fragment key={message.id}>
                                    <Chat.AiMessage
                                        withCopy
                                        onRegenerate={
                                            idx === messages.length - 1
                                                ? reload
                                                : undefined
                                        }
                                        onFeedback={
                                            status === "ready" ||
                                            idx !== messages.length - 1
                                                ? handleScoreResponse(
                                                      message.id,
                                                  )
                                                : undefined
                                        }
                                    >
                                        {message.content}
                                    </Chat.AiMessage>
                                    {idx === messages.length - 1 &&
                                        status === "streaming" && (
                                            <LoadingText message={message} />
                                        )}
                                </Fragment>
                            ) : (
                                <Chat.UserMessage
                                    key={message.id}
                                    scrollToMessage={
                                        idx === messages.length - 1
                                    }
                                    attachment={
                                        message.experimental_attachments?.[0]
                                    }
                                >
                                    {message.content}
                                </Chat.UserMessage>
                            ),
                        )}
                        {isLoading && <Chat.LoadingMessage />}
                        {!!error && (
                            <Chat.ErrorMessage>
                                {t(keys.error_message)}
                            </Chat.ErrorMessage>
                        )}
                    </Chat.Content>
                )}
                <Chat.ScrollToBottomButton />
                <Chat.Input
                    onSubmit={handleSubmit}
                    onFileChange={setFile}
                    uploadedFile={file}
                    acceptedFileTypes=".pdf,.doc,.docx,.xls,.xlsx"
                    onInputChange={handleInputChange}
                    loading={isUploadLoading}
                    input={input}
                    disabled={isDisabled}
                    placeholder={t(keys.input_placeholder)}
                    onStop={showStopButton ? stop : undefined}
                />
                {!messages.length && (
                    <Chat.ExampleMessageSection>
                        {exampleMessages.map((message) => (
                            <Chat.ExampleMessageChip
                                key={message}
                                onClick={handleExampleMessageChipClick(message)}
                            >
                                {message}
                            </Chat.ExampleMessageChip>
                        ))}
                    </Chat.ExampleMessageSection>
                )}
            </Chat>
        </FlexCol>
    );
};

const LoadingText: FC<{ message: Message }> = ({ message }) => {
    const isToolInvocation =
        message.parts?.[message.parts.length - 1]?.type === "tool-invocation";
    if (!isToolInvocation) {
        return null;
    }
    const toolInvocation = message.parts?.find(
        (part) => part.type === "tool-invocation",
    );
    return (
        <Chat.LoadingMessage>
            {toolInvocation?.toolInvocation?.args?.loadingText}
        </Chat.LoadingMessage>
    );
};
